(() => {
  const CONTEXT_ATTRIBUTE = 'site_context';
  const VALID_CONTEXTS = new Set(['PF', 'PJ']);

  function normalizeContext(value) {
    const context = String(value || '').trim().toUpperCase();
    return VALID_CONTEXTS.has(context) ? context : null;
  }

  function isAuthenticated() {
    return document.body?.dataset.elevaAuthenticated === 'true';
  }

  function getResolvedContext() {
    return normalizeContext(document.body?.dataset.elevaContext);
  }

  function cartContextValue(profile) {
    return normalizeContext(profile)?.toLowerCase() || null;
  }

  async function updateCart({ profile, updates } = {}) {
    const context = cartContextValue(profile);
    if (!context) throw new Error('Contexto de perfil inválido.');

    const body = { attributes: { [CONTEXT_ATTRIBUTE]: context } };
    if (updates && Object.keys(updates).length > 0) body.updates = updates;

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    const cart = await response.json();
    if (!response.ok) {
      throw new Error(cart?.description || cart?.message || 'Não foi possível atualizar o carrinho.');
    }

    return cart;
  }

  async function getCartProductFlags() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-product-flags`, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Não foi possível consultar os produtos do carrinho.');

    const html = await response.text();
    const section = new DOMParser().parseFromString(html, 'text/html');
    const data = section.querySelector('[data-eleva-cart-product-flags]');
    if (!data) throw new Error('Não foi possível identificar os produtos do carrinho.');

    const items = JSON.parse(data.textContent);
    if (!Array.isArray(items) || items.some((item) => !item || !item.key)) {
      throw new Error('Os dados dos produtos do carrinho são inválidos.');
    }
    return items;
  }

  function getIncompatibleUpdates(profile, items) {
    const updates = {};
    const removedItems = [];

    (items || []).forEach((item) => {
      const incompatible =
        (profile === 'PF' && item.toolbox === true) ||
        (profile === 'PJ' && item.curso === true);

      if (!incompatible || !item.key) return;

      updates[item.key] = 0;
      removedItems.push({
        key: String(item.key),
        title: String(item.title || ''),
        reason: profile === 'PF' ? 'incompatible_with_pf' : 'incompatible_with_pj',
      });
    });

    return { updates, removedItems };
  }

  function notifyCartChange(cart, removedItems, source) {
    document.dispatchEvent(new CustomEvent('cart:change', {
      detail: { cart, removedItems, source },
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  async function setGuestContext(profile) {
    if (isAuthenticated()) {
      throw new Error('O perfil de um cliente autenticado é definido pelo cadastro.');
    }

    const context = normalizeContext(profile);
    if (!context) throw new Error('Contexto de perfil inválido.');

    await updateCart({ profile: context });
    return context;
  }

  async function prepareGuestContext(profile) {
    const context = normalizeContext(profile);
    if (!context) throw new Error('Contexto de perfil inválido.');

    await updateCart({ profile: context });
    return context;
  }

  async function switchCartContext(profile) {
    const context = normalizeContext(profile);
    if (!context) throw new Error('Contexto de perfil inválido.');

    const items = await getCartProductFlags();
    const { updates, removedItems } = getIncompatibleUpdates(context, items);
    const updatedCart = await updateCart({ profile: context, updates });
    notifyCartChange(updatedCart, removedItems, 'context-switch');

    return { context, cart: updatedCart, removedItems };
  }

  async function switchGuestContext(profile) {
    if (isAuthenticated()) {
      throw new Error('O perfil de um cliente autenticado precisa encerrar a sessão para ser trocado.');
    }

    return switchCartContext(profile);
  }

  async function logout(logoutUrl) {
    const response = await fetch(logoutUrl || '/account/logout', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error('Não foi possível encerrar a sessão.');
  }

  async function chooseContext(profile) {
    const context = await setGuestContext(profile);
    window.location.reload();
    return context;
  }

  async function changeContext(profile, logoutUrl) {
    const authenticated = isAuthenticated();
    const result = authenticated
      ? await switchCartContext(profile)
      : await switchGuestContext(profile);

    if (authenticated) await logout(logoutUrl);

    window.location.reload();
    return result;
  }

  window.ElevaContext = {
    get: getResolvedContext,
    set: setGuestContext,
    prepareGuest: prepareGuestContext,
    choose: chooseContext,
    switch: switchGuestContext,
    change: changeContext,
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-context-type]').forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        try {
          button.disabled = true;
          await chooseContext(button.dataset.contextType);
        } catch (error) {
          console.error('[eleva-context]', error);
          button.disabled = false;
        }
      });
    });

    document.querySelectorAll('[data-context-switch-to]').forEach((button) => {
      button.addEventListener('click', async () => {
        try {
          button.disabled = true;
          await changeContext(button.dataset.contextSwitchTo, button.dataset.contextLogoutUrl);
        } catch (error) {
          console.error('[eleva-context]', error);
          button.disabled = false;
        }
      });
    });
  });
})();
